/**
 * What an external system has to say about the interval on screen.
 *
 * Only ever appears for a source that needs something from the user or failed:
 * a working provider says nothing and just contributes rows. It sits above the
 * timeline rather than replacing it, because a dead Gmail grant must not hide
 * the ordinary records loaded beside it.
 */

import { useState } from "react";
import { AlertTriangle, Link2Off, Plug } from "lucide-react";

import { useSetCollectionProviderMutation } from "@/api/api";
import { Button } from "@/components/ui/button";
import { ExternalSourceStatus } from "@/providers/external/source";
import {
  connectErrorMessage,
  useConnectProvider,
} from "@/providers/external/connect";
import { getProvider } from "@/providers/external/registry";

function ConnectRow({ status }: { status: ExternalSourceStatus }) {
  const connect = useConnectProvider();
  const [setCollectionProvider] = useSetCollectionProviderMutation();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const provider = getProvider(status.providerKey);
  if (!provider) return null;

  const Icon = provider.Icon;

  const onConnect = async () => {
    setBusy(true);
    setProblem(null);
    const result = await connect(provider.systemId, provider.scopes);
    if (result.status === "ok" && result.connection) {
      // The grant exists now; this is what points *this* collection at it. The
      // server binds the whole provider tree, so the subcollections come along
      // and are never authorized separately.
      await setCollectionProvider({
        collectionID: status.collectionId,
        connection_id: result.connection,
        set_connection: true,
      });
    }
    setProblem(connectErrorMessage(result));
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2">
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0 grow text-sm text-muted-foreground">
        {problem ?? status.message ?? `Connect ${provider.title}.`}
      </span>
      <Button size="sm" onClick={onConnect} disabled={busy}>
        <Plug className="h-4 w-4" />
        {busy ? "Connecting…" : `Connect ${provider.title}`}
      </Button>
    </div>
  );
}

function ProblemRow({ status }: { status: ExternalSourceStatus }) {
  const Icon = status.state === "unsupported" ? Link2Off : AlertTriangle;
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0">
        {status.title}: {status.message ?? "could not be read."}
      </span>
    </div>
  );
}

function dedupeByProvider(statuses: ExternalSourceStatus[]) {
  const seen = new Set<string>();
  return statuses.filter((status) => {
    if (seen.has(status.providerKey)) return false;
    seen.add(status.providerKey);
    return true;
  });
}

export default function ProviderBand({
  statuses,
  inCollection,
}: {
  statuses: ExternalSourceStatus[];
  /** False on the all-collections timeline, which owns no provider. */
  inCollection: boolean;
}) {
  // "empty" and "coarse" are silent here on purpose. Neither is a fault, and a
  // band row is the wrong shape for either: the first has nothing to say at
  // all, and the second is the reason the list below is empty, so it is `Records`
  // that says it, in the middle of the empty list where the rows would be.
  //
  // One row per provider, not per collection. A Gmail collection has six
  // subcollections sharing one grant, and six identical "Connect Gmail"
  // prompts would be six ways to do the same thing. Connecting any one of them
  // binds the whole tree, so the first is as good as any.
  //
  // The connect prompt is suppressed outside a collection. On the
  // all-collections timeline it is an ask with no context -- the page names no
  // provider, and the place to grant access is the collection that needs it.
  const notable = dedupeByProvider(
    statuses.filter(
      (status) =>
        (status.state === "auth" && inCollection) ||
        status.state === "error" ||
        status.state === "unsupported",
    ),
  );
  const truncated = dedupeByProvider(
    statuses.filter((status) => status.truncated),
  );

  if (notable.length === 0 && truncated.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 px-2 pb-2">
      {notable.map((status) =>
        status.state === "auth" ? (
          <ConnectRow key={status.collectionId} status={status} />
        ) : (
          <ProblemRow key={status.collectionId} status={status} />
        ),
      )}
      {truncated.map((status) => (
        <div
          key={`${status.collectionId}-truncated`}
          className="px-1 text-xs text-muted-foreground"
        >
          Showing the first results from {status.title}. Narrow the range to see
          the rest.
        </div>
      ))}
    </div>
  );
}
